import { TextBrandProps, getTextStylesFromBrandStyle, tryToGetBrandColorRefByHEX } from 'modules/BrandDefinition';
import * as Models from 'models';
import { TextBrandStyleField as StyleField } from 'models';
import { createLexicalTextEditor } from '../../editors';
import * as listUtils from '../../Plugins/CustomListPlugin/utils';
import { $verifyStyleForFont } from '../../Plugins/FontBrandStylePlugin/brandStyle';
import * as fontUtils from '../../Plugins/FontPlugin/utils';
import * as textBlockUtils from '../../Plugins/TextBlockPlugin/utils';

// eslint-disable-next-line sonarjs/cognitive-complexity
export function mergeBrandStylesOnComponent(
  textComponent: Models.TextComponent,
  layoutBrandProps: TextBrandProps,
  projectBrandProps: TextBrandProps,
): Models.TextComponent {
  const { lexicalState } = textComponent;
  if (!lexicalState) {
    return textComponent;
  }

  const layoutTextStyles = getTextStylesFromBrandStyle(
    layoutBrandProps.brandStyle,
    layoutBrandProps.colors,
    layoutBrandProps.fonts,
  );
  const projectTextStyles = getTextStylesFromBrandStyle(
    projectBrandProps.brandStyle,
    projectBrandProps.colors,
    projectBrandProps.fonts,
  );

  const editor = createLexicalTextEditor(lexicalState);
  editor.update(() => {
    // BULLET_COLOR
    if (listUtils.$doAllNodesHaveBulletColorHex(layoutTextStyles[StyleField.BULLET_COLOR])) {
      const projectBulletColor = projectTextStyles[StyleField.BULLET_COLOR];
      // FIXME: an error may arise due to hex to BrandColorRef conversion
      const color = tryToGetBrandColorRefByHEX(projectBrandProps.colors, projectBulletColor);
      listUtils.$applyBulletColorToAllNodes(color);
    }

    // verify that css values corresponds to brandColor and brandFont of the reusable layout
    // draftjs may have wrong/confusing values in inline style,
    // so if the document come after conversion it may have wrong font color hex value (at least)
    $verifyStyleForFont(layoutBrandProps.colors, layoutBrandProps.fonts);

    // FONT_SIZE
    if (fontUtils.$doAllNodesHaveFontSize(layoutTextStyles[StyleField.FONT_SIZE], true)) {
      const projectFontSize = projectTextStyles[StyleField.FONT_SIZE];
      fontUtils.$applyFontSizeToAllNodes(projectFontSize);
      fontUtils.$updateFontSizeForAllParagraphs();
    }

    // BRAND FONT NAME
    if (fontUtils.$doAllNodesHaveBrandFontName(layoutTextStyles[StyleField.FONT_FAMILY], true)) {
      const projectBrandFontName = projectTextStyles[StyleField.FONT_FAMILY];
      fontUtils.$applyBrandFontNameToAllNodes(projectBrandFontName);
    }

    /**
     * BRAND FONT CHAR STYLE
     * draftjs provides string value "undefined" as a character style, which lexical converter strips out
     * so it will be incorrect to skip empty values for character style.
     */
    if (fontUtils.$doAllNodesHaveBrandFontCharStyle(layoutTextStyles[StyleField.CHARACTER_STYLE], false)) {
      const projectBrandFontCharStyle = projectTextStyles[StyleField.CHARACTER_STYLE];
      fontUtils.$applyBrandFontCharStyleToAllNodes(projectBrandFontCharStyle);
    }

    /**
     * FONT COLOR
     * FIXME: the logic is the same as for draftjs
     * layoutTextStyles[StyleField.FONT_COLOR] (which is usually hex) is compared to brandColorName (which corresponds to name not to hex)
     * these comparison will be faulty, so the color will never correspond to projectTextStyles[StyleField.FONT_COLOR]
     */
    if (fontUtils.$doAllNodesHaveBrandColorName(layoutTextStyles[StyleField.FONT_COLOR])) {
      const projectFontColorHex = projectTextStyles[StyleField.FONT_COLOR] ?? '';
      // FIXME: an error may arise due to hex to BrandColorRef conversion
      const color = tryToGetBrandColorRefByHEX(projectBrandProps.colors, projectFontColorHex);
      fontUtils.$applyFontBrandColorToAllNodes(projectBrandProps.colors, color);
    }

    // to sync css values to brandColor and brandFont of the project
    $verifyStyleForFont(projectBrandProps.colors, projectBrandProps.fonts);

    // TEXT ALIGN
    if (textBlockUtils.$doAllNodesHaveHorizontalAlignment(layoutTextStyles[StyleField.TEXT_ALIGN])) {
      const projectTextAlign = projectTextStyles[StyleField.TEXT_ALIGN];
      textBlockUtils.$applyHorizontalAlignmentToAllNodes(projectTextAlign);
    }

    // LINE HEIGHT
    if (textBlockUtils.$doAllNodesHaveLineHeight(layoutTextStyles[StyleField.LINE_HEIGHT], true)) {
      const projectLineHeight = projectTextStyles[StyleField.LINE_HEIGHT];
      textBlockUtils.$applyLineHeightToAllNodes(projectLineHeight);
    }
  }, { discrete: true });

  return {
    ...textComponent,
    lexicalState: JSON.stringify(editor.getEditorState()),
  };
}
